import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  AlertTriangle,
  Bell,
  CheckCircle2,
  DownloadCloud,
  LifeBuoy,
  Loader2,
  Menu,
  MessageCircle,
  PauseCircle,
  Search,
  UserPlus,
  X,
} from "lucide-react";
import { api, apiError, formatDate } from "@/lib/api";
import { MESSAGES_CHANGED, SUPPORT_CHANGED, onChange } from "@/lib/events";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

const ICONS = {
  ticket: LifeBuoy,
  reply: LifeBuoy,
  setup: UserPlus,
  suspended: PauseCircle,
  message: MessageCircle,
};

function ago(value) {
  if (!value) return "";
  const then = new Date(value);
  if (Number.isNaN(then.getTime())) return "";
  const mins = Math.floor((Date.now() - then.getTime()) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days === 1) return "yesterday";
  if (days < 30) return `${days}d ago`;
  return `${Math.floor(days / 30)}mo ago`;
}

function IconButton({ icon: Icon, count, tone = "brand", label, children }) {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          aria-label={label}
          title={label}
          className="relative p-2 rounded-lg text-muted2 hover:text-ink hover:bg-card transition-colors"
        >
          <Icon className="w-[18px] h-[18px]" />
          {count > 0 && (
            <span
              className={cn(
                "absolute -top-0.5 -right-0.5 min-w-[16px] h-[16px] px-1 rounded-full text-white text-[10px] font-mono flex items-center justify-center",
                tone === "warn" ? "bg-stock-low" : "bg-brand"
              )}
            >
              {count > 9 ? "9+" : count}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[330px] p-0">
        {typeof children === "function" ? children(() => setOpen(false)) : children}
      </PopoverContent>
    </Popover>
  );
}

/**
 * One bell, two sources. The owner sees platform-wide things; a client sees
 * replies to their own messages and stock that's run out.
 */
export function NotificationBell({ scope = "tenant" }) {
  const navigate = useNavigate();
  const [data, setData] = useState({ count: 0, items: [] });
  const endpoint = scope === "platform" ? "/platform/notifications" : "/notifications";

  const load = useCallback(async () => {
    try {
      const res = await api.get(endpoint);
      setData(res.data);
    } catch {
      /* a failed poll shouldn't shout at anyone */
    }
  }, [endpoint]);

  useEffect(() => {
    load();
    // Was 20s — noticeably slower than the sidebar's own unread badge
    // (3s), so a new message would show up in the conversation list well
    // before this bell caught up to it. 5s keeps it close to that without
    // polling as often as the badge, since this panel isn't watched as
    // constantly.
    const timer = setInterval(load, 5000);
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    const offSupport = onChange(SUPPORT_CHANGED, load);
    // A client's bell includes unread conversations (see /notifications on
    // the backend) — without this it only caught up every 20s on its own
    // poll, so a new message sat unseen in the bell until that timer fired
    // or the page was reloaded. This is the same push-driven bump the
    // sidebar's unread badge and Messages.jsx's own poll already use.
    const offMessages = onChange(MESSAGES_CHANGED, load);
    return () => {
      clearInterval(timer);
      window.removeEventListener("focus", onFocus);
      offSupport();
      offMessages();
    };
  }, [load]);

  // Reading one means opening it, not glancing at the list. Clicking an item
  // marks that one and drops the count by however many messages it was
  // carrying (unread_count — a conversation with 3 new texts is 3, not 1),
  // not just one; the rest stay unread.
  const markOne = useCallback(async (item) => {
    if (!item.unread) return;
    setData((d) => ({
      count: Math.max((d.count || 0) - (item.unread_count || 1), 0),
      items: (d.items || []).map((i) =>
        i.id === item.id ? { ...i, unread: false } : i
      ),
    }));
    try {
      await api.post("/notifications/read", {
        items: [{ id: item.id, stamp: item.stamp }],
      });
    } catch {
      /* the next poll will put it back if this didn't land */
    }
  }, []);

  const markAll = useCallback(async () => {
    const unread = (data.items || []).filter((i) => i.unread);
    if (unread.length === 0) return;
    setData((d) => ({
      count: 0,
      items: (d.items || []).map((i) => ({ ...i, unread: false })),
    }));
    try {
      await api.post("/notifications/read", {
        items: unread.map((i) => ({ id: i.id, stamp: i.stamp })),
      });
    } catch {
      /* not worth interrupting anyone over */
    }
  }, [data.items]);

  return (
    <IconButton icon={Bell} count={data.count} label="Notifications">
      {(close) => (
        <>
          <div className="px-4 py-3 border-b border-line flex items-center gap-2">
            <div className="eyebrow">Notifications</div>
            {data.count > 0 && (
              <button
                onClick={markAll}
                className="ml-auto text-[11.5px] link-quiet"
              >
                Mark all read
              </button>
            )}
          </div>
          {data.items.length === 0 ? (
            <div className="px-4 py-10 text-center">
              <CheckCircle2 className="w-5 h-5 text-stock-in mx-auto mb-2.5" />
              <p className="text-[13px] text-muted2">Nothing needs you right now.</p>
            </div>
          ) : (
            <div className="max-h-[340px] overflow-y-auto divide-y divide-line/70">
              {data.items.map((n) => {
                const Icon = ICONS[n.kind] || Bell;
                return (
                  <button
                    key={n.id}
                    onClick={() => {
                      markOne(n);
                      close();
                      navigate(n.href);
                    }}
                    className={cn(
                      "w-full text-left px-4 py-3 hover:bg-brand-light/50 transition-colors flex gap-3",
                      n.unread && "bg-brand-light/30"
                    )}
                  >
                    <Icon
                      className={cn(
                        "w-4 h-4 shrink-0 mt-0.5",
                        n.severity === "high" ? "text-stock-out" : "text-muted2"
                      )}
                    />
                    <div className="min-w-0 flex-1">
                      <div
                        className={cn(
                          "text-[13px] leading-snug text-ink",
                          n.unread ? "font-semibold" : "font-medium"
                        )}
                      >
                        {n.title}
                      </div>
                      <div className="text-[12px] text-muted2 mt-0.5 line-clamp-2 leading-snug">
                        {n.body}
                      </div>
                      {n.at && <div className="text-[11px] text-muted2 mt-1">{ago(n.at)}</div>}
                    </div>
                    {n.unread && (
                      <span className="w-1.5 h-1.5 rounded-full bg-brand shrink-0 mt-1.5" />
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </>
      )}
    </IconButton>
  );
}

export function BackupBell() {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api.get("/admin/backup/status");
      setStatus(res.data);
    } catch {
      /* quiet */
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const take = async (close) => {
    setBusy(true);
    try {
      const res = await api.get("/admin/backup", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/json" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `backup-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Backup downloaded — store it somewhere safe");
      await load();
      close?.();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const overdue = !!status?.overdue;

  return (
    <IconButton icon={DownloadCloud} count={overdue ? 1 : 0} tone="warn" label="Backup">
      {(close) => (
        <>
          <div className="px-4 py-3 border-b border-line">
            <div className="eyebrow">Backup</div>
          </div>
          <div className="px-4 py-4">
            <div className="flex gap-2.5 mb-3">
              {overdue ? (
                <>
                  <AlertTriangle className="w-4 h-4 text-stock-low shrink-0 mt-0.5" />
                  <p className="text-[12.5px] text-ink leading-relaxed">
                    {status?.last_backup_at
                      ? `Last taken ${status.days_since} days ago.`
                      : "You haven't taken one yet."}{" "}
                    This file is the only copy of your clients' conversations.
                  </p>
                </>
              ) : (
                <>
                  <CheckCircle2 className="w-4 h-4 text-stock-in shrink-0 mt-0.5" />
                  <p className="text-[12.5px] text-ink leading-relaxed">
                    Last taken {formatDate(status?.last_backup_at)}.
                  </p>
                </>
              )}
            </div>
            <Button
              onClick={() => take(close)}
              disabled={busy}
              className="w-full bg-brand hover:bg-brand-hover"
            >
              {busy ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  <DownloadCloud className="w-4 h-4 mr-2" /> Download now
                </>
              )}
            </Button>
            <p className="text-[11.5px] text-muted2 mt-3 leading-relaxed">
              Reminder frequency is in Platform settings.
            </p>
          </div>
        </>
      )}
    </IconButton>
  );
}

/**
 * Search pill.
 *
 * The pill *is* the input rather than a button that opens one — having a second
 * search box appear below the first looked like a duplicate. Results drop
 * underneath as you type.
 */
export function GlobalSearch({ scope = "tenant", placeholder = "Search" }) {
  const navigate = useNavigate();
  const [term, setTerm] = useState("");
  const [groups, setGroups] = useState([]);
  const [searching, setSearching] = useState(false);
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const endpoint = scope === "platform" ? "/platform/search" : "/search";

  // Close when clicking away or pressing Escape.
  useEffect(() => {
    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, []);

  // Wait for a pause in typing rather than firing on every keystroke.
  useEffect(() => {
    if (term.trim().length < 2) {
      setGroups([]);
      return undefined;
    }
    let cancelled = false;
    setSearching(true);
    const timer = setTimeout(async () => {
      try {
        const res = await api.get(endpoint, { params: { q: term } });
        if (!cancelled) setGroups(res.data.groups || []);
      } catch {
        if (!cancelled) setGroups([]);
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [term, endpoint]);

  const go = (href) => {
    setOpen(false);
    setTerm("");
    navigate(href);
  };

  const total = groups.reduce((n, g) => n + g.items.length, 0);
  const showPanel = open && term.trim().length > 0;

  return (
    <div ref={wrapRef} className="relative w-full max-w-[320px]">
      <div className="relative">
        <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted2 pointer-events-none" />
        <input
          value={term}
          onChange={(e) => {
            setTerm(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          placeholder={placeholder}
          aria-label={placeholder}
          className="w-full h-9 pl-9 pr-8 rounded-full border border-line bg-card text-[13px] text-ink placeholder:text-muted2 focus:outline-none focus:border-brand focus:ring-2 focus:ring-brand/20 transition-colors"
        />
        {term && (
          <button
            onClick={() => {
              setTerm("");
              setOpen(false);
            }}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted2 hover:text-ink"
            aria-label="Clear search"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {showPanel && (
        <div className="absolute top-[calc(100%+6px)] left-0 right-0 bg-card border border-line rounded-xl shadow-lift overflow-hidden z-50">
          <div className="max-h-[320px] overflow-y-auto">
            {term.trim().length < 2 ? (
              <p className="px-4 py-6 text-center text-[12.5px] text-muted2">
                Keep typing — two letters or more.
              </p>
            ) : searching ? (
              <div className="px-4 py-6 flex justify-center">
                <Loader2 className="w-4 h-4 animate-spin text-brand" />
              </div>
            ) : total === 0 ? (
              <p className="px-4 py-6 text-center text-[12.5px] text-muted2">
                Nothing matches “{term}”.
              </p>
            ) : (
              groups.map((g) => (
                <div key={g.label}>
                  <div className="px-4 py-2 bg-surface/60 border-b border-line/70">
                    <div className="eyebrow">{g.label}</div>
                  </div>
                  {g.items.map((it) => (
                    <button
                      key={`${g.label}-${it.id}`}
                      onClick={() => go(it.href)}
                      className="w-full text-left px-4 py-2.5 hover:bg-brand-light/50 transition-colors border-b border-line/50 last:border-0"
                    >
                      <div className="text-[13px] text-ink truncate">{it.title}</div>
                      {it.sub && (
                        <div className="text-[11.5px] text-muted2 truncate mt-0.5">{it.sub}</div>
                      )}
                    </button>
                  ))}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * The shared top bar.
 *
 * Title left, search centre, icons right. When the window is too narrow, the
 * page's own action drops to a second row rather than squeezing the icons —
 * reaching notifications matters more than keeping everything on one line.
 */
export function TopBar({
  title,
  subtitle,
  action,
  onMenu,
  menuOpen,
  scope = "tenant",
  searchPlaceholder = "Search",
  showBackup = false,
}) {
  return (
    <header className="sticky top-0 bg-surface border-b border-line z-20">
      <div className="flex items-center gap-3 px-4 sm:px-7 min-h-[68px] flex-wrap lg:flex-nowrap py-2.5 lg:py-0">
        <button
          className="lg:hidden p-2 -ml-2 rounded-lg text-muted2 hover:bg-card shrink-0 order-1"
          onClick={onMenu}
          aria-label="Toggle navigation"
        >
          {menuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>

        <div className="min-w-0 flex-1 order-2">
          <h1 className="page-title truncate">{title}</h1>
          {subtitle && <p className="text-[12.5px] text-muted2 truncate mt-0.5">{subtitle}</p>}
        </div>

        <div className="hidden md:flex justify-center flex-1 min-w-0 order-3 px-2">
          <GlobalSearch scope={scope} placeholder={searchPlaceholder} />
        </div>

        {/* Icons never shrink and never wrap. */}
        <div className="flex items-center gap-0.5 shrink-0 order-4">
          {showBackup && <BackupBell />}
          <NotificationBell scope={scope} />
        </div>

        {action && (
          <div className="flex items-center gap-2 shrink-0 w-full lg:w-auto order-5 lg:order-3">
            {action}
          </div>
        )}
      </div>

      <div className="md:hidden px-4 pb-2.5">
        <GlobalSearch scope={scope} placeholder={searchPlaceholder} />
      </div>
    </header>
  );
}
